/**
 * Boundary of the theme editor to cms-service (and catalog for preview data). Screens only talk to
 * this interface, so tests inject a fake and the real one wraps the generated OpenAPI clients
 * (`@tcg/api-client` — cms T09-19, catalog T02). Gateway: `/api/v1/admin/*` → service `/v1/admin/*`.
 */
import {
  type CmsPaths,
  createCatalogClient,
  createCmsClient,
  createNotificationClient,
  idempotencyKey,
} from "@tcg/api-client";

/** JSON body of the success answer (200, else 201) of `method path` in the cms OpenAPI. */
type JsonOf<P extends keyof CmsPaths, M extends "get" | "post" | "put" | "patch"> =
  NonNullable<CmsPaths[P][M]> extends { responses: infer R }
    ? R extends { 200: { content: { "application/json": infer B } } }
      ? B
      : R extends { 201: { content: { "application/json": infer B } } }
        ? B
        : never
    : never;

export type ThemeOut = JsonOf<"/v1/admin/themes/{theme_id}", "get">;
export type ManifestOut = JsonOf<"/v1/admin/theme/sections", "get">;
export type TemplateSummaryOut = JsonOf<
  "/v1/admin/themes/{theme_id}/templates",
  "get"
>["data"][number];
export type TemplateOut = JsonOf<"/v1/admin/themes/{theme_id}/templates/{key}", "get">;
export type SectionGroupOut = JsonOf<"/v1/admin/themes/{theme_id}/section-groups/{name}", "get">;
export type PreviewTokenCreatedOut = JsonOf<"/v1/admin/themes/{theme_id}/preview-tokens", "post">;
export type PreviewTokenOut = JsonOf<
  "/v1/admin/themes/{theme_id}/preview-tokens/{token_id}/renew",
  "post"
>;
export type PublishOut = JsonOf<"/v1/admin/themes/{theme_id}/publish", "post">;
export type IssueOut = NonNullable<TemplateOut["issues"]>[number];
export type ThemeSettingsOut = JsonOf<"/v1/admin/themes/{theme_id}/settings", "get">;
export type ThemeDiffOut = JsonOf<"/v1/admin/themes/{theme_id}/diff", "get">;
export type PublishCheckOut = JsonOf<"/v1/admin/themes/{theme_id}/publish-check", "get">;
export type ThemeVersionOut = JsonOf<"/v1/admin/themes/{theme_id}/versions", "get">["data"][number];
export type RollbackOut = JsonOf<
  "/v1/admin/themes/{theme_id}/versions/{version_no}/rollback",
  "post"
>;
export type RevalidationListOut = JsonOf<"/v1/admin/system/revalidations", "get">;
export type RevalidationOut = RevalidationListOut["data"][number];
export type SuggestionOut = JsonOf<"/v1/admin/translations/suggest", "post">["data"][number];
export type MediaItemOut = JsonOf<"/v1/admin/media-library", "get">["data"][number];
type TemplateBodyIn = NonNullable<
  NonNullable<CmsPaths["/v1/admin/themes/{theme_id}/templates/{key}"]["put"]>["requestBody"]
>["content"]["application/json"];

export type SectionGroupName = "header" | "footer" | "announcement" | "overlay";

/** A product / collection offered as preview data (design/03 §5 "Preview data"). */
export interface PreviewResource {
  kind: "product" | "collection";
  handle: string;
  title: string;
  /** Rank A–D / grade for cards — staff must pick the right copy (R16). */
  detail: string | null;
  imageUrl: string | null;
}

/** One choice of a picker (product, category, page, menu, rule page, product field, picture). */
export interface PickerItem {
  /** Stored value: `product:pikachu-sv1s`, `collection:raw`, `media:<uuid>`, `attribute:custom.detail`. */
  ref: string;
  title: string;
  /** Rank A–D / grade for cards (R16), item count, file size … */
  detail: string | null;
  imageUrl: string | null;
  /** Sale state in plain data: `active`, `draft`, `sold_out`, `published` … */
  status: string | null;
}

export type PickerKind =
  "product" | "collection" | "page" | "policy" | "menu" | "attribute" | "media";

export interface PublishOptions {
  note?: string | null;
  /** RFC 3339 with offset — a scheduled publish (T09-25). */
  publishAt?: string | null;
  /** One key per publish intent (`useIntentKeys`) so a retry after a lost answer replays. */
  idempotencyKey?: string;
}

/** Data the setting fields need (T09-23) — a small interface so field tests use a tiny fake. */
export interface PickerApi {
  /** Search one kind of thing; `q` empty = the first page. Media: `mediaKind` narrows files. */
  searchPicker(
    kind: PickerKind,
    q: string,
    options?: { mediaKind?: "image" | "video" | "file" },
  ): Promise<PickerItem[]>;
  /** Details of stored refs (to show the picked product's picture/name); unknown refs are left out. */
  describeRefs(refs: string[]): Promise<PickerItem[]>;
  /** Shop languages that are switched on (`GET /admin/locales`), default language first. */
  listEnabledLocales(): Promise<string[]>;
  /** Machine translation draft; throws `ApiError` 501 when not set up (stub, T09-13). */
  suggestTranslation(text: string, sourceLocale: string, targetLocale: string): Promise<string>;
}

/** An error answer of a service (`ErrorEnvelope`) or a network failure (`status` 0). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details: Array<Record<string, unknown>> = [],
  ) {
    super(`${String(status)} ${code}`);
    this.name = "ApiError";
  }
}

export interface ThemeEditorApi extends PickerApi {
  listThemes(): Promise<ThemeOut[]>;
  getTheme(themeId: string): Promise<ThemeOut>;
  duplicateTheme(themeId: string, name?: string): Promise<ThemeOut>;
  /** Publish now, or at `publishAt` (202 scheduled). Idempotency-Key = `options.idempotencyKey`. */
  publishTheme(themeId: string, options?: PublishOptions): Promise<PublishOut>;
  // ---- theme settings (T09-24) ----
  getThemeSettings(themeId: string): Promise<ThemeSettingsOut>;
  /** JSON merge patch with `If-Match` = settings version (409 on conflict). */
  patchThemeSettings(
    themeId: string,
    patch: Record<string, unknown>,
    version: number | null,
  ): Promise<ThemeSettingsOut>;
  // ---- publish / versions (T09-25) ----
  getDiff(themeId: string, against?: "published" | number): Promise<ThemeDiffOut>;
  publishCheck(themeId: string): Promise<PublishCheckOut>;
  cancelSchedule(themeId: string): Promise<void>;
  listVersions(themeId: string): Promise<ThemeVersionOut[]>;
  rollback(
    themeId: string,
    versionNo: number,
    restoreDraft: boolean,
    idempotencyKey?: string,
  ): Promise<RollbackOut>;
  listRevalidations(): Promise<RevalidationListOut>;
  retryRevalidation(revalidationId: string): Promise<RevalidationOut>;
  /** Staff without `cms.publish`: a to-do in the managers' inbox (T12, flag `VITE_PUBLISH_REQUESTS`). */
  requestPublish(input: {
    title: string;
    summary: string;
    themeId: string;
    themeName: string;
    /** Same key on a retry → one to-do, not two. */
    idempotencyKey?: string;
  }): Promise<void>;
  /** `null` = no manifest uploaded yet (the storefront has never been built). */
  getManifest(): Promise<ManifestOut | null>;
  listTemplates(themeId: string): Promise<TemplateSummaryOut[]>;
  getTemplate(themeId: string, key: string): Promise<TemplateOut>;
  putTemplate(
    themeId: string,
    key: string,
    body: Record<string, unknown>,
    version: number | null,
  ): Promise<TemplateOut>;
  /** `null` = the group was never saved (PUT without If-Match creates it). */
  getSectionGroup(themeId: string, name: SectionGroupName): Promise<SectionGroupOut | null>;
  putSectionGroup(
    themeId: string,
    name: SectionGroupName,
    body: Record<string, unknown>,
    version: number | null,
  ): Promise<SectionGroupOut>;
  createPreviewToken(themeId: string, path: string): Promise<PreviewTokenCreatedOut>;
  renewPreviewToken(themeId: string, tokenId: string): Promise<PreviewTokenOut>;
  /** Revoke a draft-read token when the screen that asked for it closes (`keepalive` on pagehide). */
  revokePreviewToken(
    themeId: string,
    tokenId: string,
    options?: { keepalive?: boolean },
  ): Promise<void>;
  searchPreviewResources(kind: "product" | "collection", query: string): Promise<PreviewResource[]>;
}

export interface ThemeEditorApiOptions {
  /** Gateway root, e.g. `/api` (paths of the clients start with `/v1/`). */
  baseUrl: string;
  getAccessToken: () => string | null | undefined;
  getLocale?: () => string | undefined;
  fetch?: typeof globalThis.fetch;
}

export interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

export function errorOf(result: { error?: unknown; response: Response }): ApiError {
  const body = result.error as { error?: { code?: unknown; details?: unknown } } | undefined;
  const code =
    typeof body?.error?.code === "string"
      ? body.error.code
      : `http_${String(result.response.status)}`;
  const details = Array.isArray(body?.error?.details)
    ? (body.error.details as Array<Record<string, unknown>>)
    : [];
  return new ApiError(result.response.status, code, details);
}

export async function unwrap<T>(request: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await request;
  } catch (error: unknown) {
    throw new ApiError(0, "network_error", [
      { message: error instanceof Error ? error.message : String(error) },
    ]);
  }
  if (result.data === undefined) {
    throw errorOf(result);
  }
  return result.data;
}

function ifMatch(version: number | null): { "If-Match"?: string } {
  return version === null ? {} : { "If-Match": `"${String(version)}"` };
}

/** The real implementation over the generated cms + catalog clients. */
export function createThemeEditorApi(options: ThemeEditorApiOptions): ThemeEditorApi {
  const common = {
    baseUrl: options.baseUrl,
    getAccessToken: options.getAccessToken,
    getLocale: options.getLocale,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  };
  const cms = createCmsClient(common);
  const catalog = createCatalogClient(common);
  const notification = createNotificationClient(common);
  const theme = (themeId: string) => ({ path: { theme_id: themeId } });
  const text = (value: Record<string, string> | undefined, fallback: string) =>
    value?.[options.getLocale?.() ?? "en"] ??
    value?.en ??
    Object.values(value ?? {})[0] ??
    fallback;

  const searchPicker: PickerApi["searchPicker"] = async (kind, query, extra) => {
    const q = query.trim() || undefined;
    switch (kind) {
      case "product": {
        const page = await unwrap(
          catalog.GET("/v1/admin/products", { params: { query: { q, limit: 20 } } }),
        );
        return page.data.map((row) => ({
          ref: `product:${row.handle}`,
          title: row.title,
          detail: row.conditionLabel || null,
          imageUrl: row.image?.url ?? null,
          status: row.status,
        }));
      }
      case "collection": {
        const page = await unwrap(
          catalog.GET("/v1/admin/collections", { params: { query: { q, limit: 20 } } }),
        );
        return page.data.map((row) => ({
          ref: `collection:${row.handle}`,
          title: row.title,
          detail: String(row.productCount),
          imageUrl: null,
          status: null,
        }));
      }
      case "page": {
        const page = await unwrap(
          cms.GET("/v1/admin/pages", { params: { query: { q, limit: 50 } } }),
        );
        return page.data.map((row) => ({
          ref: `page:${row.handle}`,
          title: text(row.title, row.handle),
          detail: null,
          imageUrl: null,
          status: row.status,
        }));
      }
      case "policy": {
        const list = await unwrap(cms.GET("/v1/admin/policies"));
        return list.data
          .filter((row) => !q || text(row.title, row.type).toLowerCase().includes(q.toLowerCase()))
          .map((row) => ({
            ref: `policy:${row.type}`,
            title: text(row.title, row.type),
            detail: null,
            imageUrl: null,
            status: row.publishedAt ? "published" : "draft",
          }));
      }
      case "menu": {
        const list = await unwrap(cms.GET("/v1/admin/menus"));
        return list.data
          .filter(
            (row) => !q || text(row.title, row.handle).toLowerCase().includes(q.toLowerCase()),
          )
          .map((row) => ({
            ref: `menu:${row.handle}`,
            title: text(row.title, row.handle),
            detail: String(row.itemCount),
            imageUrl: null,
            status: null,
          }));
      }
      case "attribute": {
        const page = await unwrap(
          catalog.GET("/v1/admin/attribute-definitions", {
            params: { query: { owner: "product", limit: 200 } },
          }),
        );
        return page.data
          .filter(
            (row) => !q || `${row.fullKey} ${row.labelEn}`.toLowerCase().includes(q.toLowerCase()),
          )
          .map((row) => ({
            ref: `attribute:${row.fullKey}`,
            title: row.labelTranslations?.[options.getLocale?.() ?? "en"] ?? row.labelEn,
            detail: row.fullKey,
            imageUrl: null,
            status: null,
          }));
      }
      case "media": {
        const page = await unwrap(
          cms.GET("/v1/admin/media-library", {
            params: { query: { q, kind: extra?.mediaKind, limit: 60 } },
          }),
        );
        return page.data.map(mediaItem);
      }
    }
  };

  return {
    searchPicker,
    describeRefs: async (refs) => {
      const out: PickerItem[] = [];
      for (const ref of refs) {
        const [kind, target] = [ref.slice(0, ref.indexOf(":")), ref.slice(ref.indexOf(":") + 1)];
        try {
          if (kind === "media") {
            const item = await unwrap(
              cms.GET("/v1/admin/media-library/{asset_id}", {
                params: { path: { asset_id: target } },
              }),
            );
            out.push(mediaItem(item));
          } else if (
            kind === "product" ||
            kind === "collection" ||
            kind === "page" ||
            kind === "policy" ||
            kind === "menu" ||
            kind === "attribute"
          ) {
            // the admin lists search by name / handle — the stored ref is matched exactly
            const found = await searchPicker(
              kind,
              kind === "policy" || kind === "menu" ? "" : target,
            );
            const hit = found.find((item) => item.ref === ref);
            if (hit) {
              out.push(hit);
            }
          }
        } catch {
          // unknown / deleted — the field shows the stored value
        }
      }
      return out;
    },
    listEnabledLocales: async () => {
      const list = await unwrap(cms.GET("/v1/admin/locales"));
      return [...list.data]
        .filter((row) => row.enabled)
        .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.position - b.position)
        .map((row) => row.code);
    },
    suggestTranslation: async (value, sourceLocale, targetLocale) => {
      const out = await unwrap(
        cms.POST("/v1/admin/translations/suggest", {
          body: { items: [{ sourceValue: value, sourceLocale, targetLocale }] },
        }),
      );
      const first = out.data[0];
      if (!first) {
        throw new ApiError(502, "no_suggestion");
      }
      return first.value;
    },
    getThemeSettings: (themeId) =>
      unwrap(cms.GET("/v1/admin/themes/{theme_id}/settings", { params: theme(themeId) })),
    patchThemeSettings: (themeId, patch, version) =>
      unwrap(
        cms.PATCH("/v1/admin/themes/{theme_id}/settings", {
          params: { ...theme(themeId), header: ifMatch(version) },
          body: patch,
        }),
      ),
    getDiff: (themeId, against = "published") =>
      unwrap(
        cms.GET("/v1/admin/themes/{theme_id}/diff", {
          params: { ...theme(themeId), query: { against: String(against) } },
        }),
      ),
    publishCheck: (themeId) =>
      unwrap(cms.GET("/v1/admin/themes/{theme_id}/publish-check", { params: theme(themeId) })),
    cancelSchedule: async (themeId) => {
      const result = await cms.DELETE("/v1/admin/themes/{theme_id}/schedule", {
        params: theme(themeId),
      });
      if (!result.response.ok) {
        throw errorOf(result);
      }
    },
    listVersions: async (themeId) =>
      (
        await unwrap(
          cms.GET("/v1/admin/themes/{theme_id}/versions", {
            params: { ...theme(themeId), query: { limit: 100 } },
          }),
        )
      ).data,
    rollback: (themeId, versionNo, restoreDraft, key) =>
      unwrap(
        cms.POST("/v1/admin/themes/{theme_id}/versions/{version_no}/rollback", {
          params: {
            path: { theme_id: themeId, version_no: versionNo },
            header: { "Idempotency-Key": key ?? idempotencyKey() },
          },
          body: { restoreDraft },
        }),
      ),
    listRevalidations: () =>
      unwrap(cms.GET("/v1/admin/system/revalidations", { params: { query: { limit: 20 } } })),
    retryRevalidation: (revalidationId) =>
      unwrap(
        cms.POST("/v1/admin/system/revalidations/{revalidation_id}/retry", {
          params: { path: { revalidation_id: revalidationId } },
        }),
      ),
    requestPublish: async ({ title, summary, themeId, themeName, idempotencyKey: key }) => {
      await unwrap(
        notification.POST("/v1/admin/inbox", {
          headers: { "Idempotency-Key": key ?? idempotencyKey() },
          body: {
            title,
            summary,
            severity: "info",
            entity: { type: "theme", id: themeId, label: themeName },
          },
        }),
      );
    },
    listThemes: async () => (await unwrap(cms.GET("/v1/admin/themes"))).data,
    getTheme: (themeId) =>
      unwrap(cms.GET("/v1/admin/themes/{theme_id}", { params: theme(themeId) })),
    duplicateTheme: (themeId, name) =>
      unwrap(
        cms.POST("/v1/admin/themes/{theme_id}/duplicate", {
          params: theme(themeId),
          body: name ? { name } : {},
        }),
      ),
    publishTheme: (themeId, publish = {}) =>
      unwrap(
        cms.POST("/v1/admin/themes/{theme_id}/publish", {
          params: {
            ...theme(themeId),
            header: { "Idempotency-Key": publish.idempotencyKey ?? idempotencyKey() },
          },
          body: {
            force: false,
            ...(publish.note ? { note: publish.note } : {}),
            ...(publish.publishAt ? { publishAt: publish.publishAt } : {}),
          },
        }),
      ),
    getManifest: async () => {
      try {
        return await unwrap(cms.GET("/v1/admin/theme/sections"));
      } catch (error: unknown) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    listTemplates: async (themeId) =>
      (await unwrap(cms.GET("/v1/admin/themes/{theme_id}/templates", { params: theme(themeId) })))
        .data,
    getTemplate: (themeId, key) =>
      unwrap(
        cms.GET("/v1/admin/themes/{theme_id}/templates/{key}", {
          params: { path: { theme_id: themeId, key } },
        }),
      ),
    putTemplate: (themeId, key, body, version) =>
      unwrap(
        cms.PUT("/v1/admin/themes/{theme_id}/templates/{key}", {
          params: { path: { theme_id: themeId, key }, header: ifMatch(version) },
          body: body as TemplateBodyIn,
        }),
      ),
    getSectionGroup: async (themeId, name) => {
      try {
        return await unwrap(
          cms.GET("/v1/admin/themes/{theme_id}/section-groups/{name}", {
            params: { path: { theme_id: themeId, name } },
          }),
        );
      } catch (error: unknown) {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    putSectionGroup: (themeId, name, body, version) =>
      unwrap(
        cms.PUT("/v1/admin/themes/{theme_id}/section-groups/{name}", {
          params: { path: { theme_id: themeId, name }, header: ifMatch(version) },
          body: body as TemplateBodyIn,
        }),
      ),
    createPreviewToken: (themeId, path) =>
      unwrap(
        cms.POST("/v1/admin/themes/{theme_id}/preview-tokens", {
          params: theme(themeId),
          body: { path },
        }),
      ),
    renewPreviewToken: (themeId, tokenId) =>
      unwrap(
        cms.POST("/v1/admin/themes/{theme_id}/preview-tokens/{token_id}/renew", {
          params: { path: { theme_id: themeId, token_id: tokenId } },
        }),
      ),
    revokePreviewToken: async (themeId, tokenId, options) => {
      const result = await cms.DELETE("/v1/admin/themes/{theme_id}/preview-tokens/{token_id}", {
        params: { path: { theme_id: themeId, token_id: tokenId } },
        ...(options?.keepalive ? { keepalive: true } : {}),
      });
      if (!result.response.ok && result.response.status !== 404) {
        throw errorOf(result);
      }
    },
    searchPreviewResources: async (kind, query) => {
      const q = query.trim() || undefined;
      if (kind === "product") {
        const page = await unwrap(
          catalog.GET("/v1/admin/products", {
            params: { query: { q, limit: 10, "filter[status]": ["active"] } },
          }),
        );
        return page.data.map((row) => ({
          kind,
          handle: row.handle,
          title: row.title,
          detail: row.conditionLabel || null,
          imageUrl: row.image?.url ?? null,
        }));
      }
      const page = await unwrap(
        catalog.GET("/v1/admin/collections", { params: { query: { q, limit: 10 } } }),
      );
      return page.data.map((row) => ({
        kind,
        handle: row.handle,
        title: row.title,
        detail: String(row.productCount),
        imageUrl: null,
      }));
    },
  };
}

function mediaItem(item: MediaItemOut): PickerItem {
  return {
    ref: `media:${item.assetId}`,
    title: item.filename ?? item.assetId,
    detail: item.width && item.height ? `${String(item.width)}×${String(item.height)}` : null,
    imageUrl: item.thumbnailUrl ?? item.url ?? null,
    status: item.readyAt ? "ready" : "processing",
  };
}

/** Gateway root for the generated clients: `/api/v1` (env) → `/api` (paths start with `/v1/`). */
export function gatewayRoot(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, "").replace(/\/v1$/, "");
}
